import React, { useEffect, useRef } from "react";
import {
  Animated,
  Easing,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  View,
} from "react-native";
import { CF } from "../theme/turquoiseTheme.js";
import { getBottomInset, getTopInset } from "../utils/safeInsets.js";
import { useLanguage } from "../i18n/LanguageContext.js";

const TOP = getTopInset();
const BOTTOM = getBottomInset();

/**
 * First-launch welcome / mission intro.
 * @param {{ onContinue: () => void }} props
 */
export function WelcomeIntroScreen({ onContinue }) {
  const fade = useRef(new Animated.Value(0)).current;
  const rise = useRef(new Animated.Value(18)).current;
  const { t, locale, setLocale } = useLanguage();

  useEffect(() => {
    Animated.parallel([
      Animated.timing(fade, {
        toValue: 1,
        duration: 700,
        easing: Easing.out(Easing.cubic),
        useNativeDriver: true,
      }),
      Animated.timing(rise, {
        toValue: 0,
        duration: 700,
        easing: Easing.out(Easing.cubic),
        useNativeDriver: true,
      }),
    ]).start();
  }, [fade, rise]);

  const paragraphs = [t("welcome_p1"), t("welcome_p2"), t("welcome_p3"), t("welcome_p4")];

  return (
    <View style={[styles.root, { paddingTop: TOP, paddingBottom: BOTTOM }]}>
      <View style={styles.glowTop} pointerEvents="none" />
      <View style={styles.glowBottom} pointerEvents="none" />

      <Animated.View style={[styles.inner, { opacity: fade, transform: [{ translateY: rise }] }]}>
        <ScrollView
          style={styles.scroll}
          contentContainerStyle={styles.scrollContent}
          showsVerticalScrollIndicator={false}
        >
          <Text style={styles.brand}>Procsystim</Text>
          <Text style={styles.tagline}>{t("welcome_tagline")}</Text>

          <View style={styles.langRow}>
            <Pressable
              style={[styles.langChip, locale === "en" && styles.langChipOn]}
              onPress={() => setLocale("en")}
            >
              <Text style={[styles.langChipText, locale === "en" && styles.langChipTextOn]}>{t("lang_english")}</Text>
            </Pressable>
            <Pressable
              style={[styles.langChip, locale === "fr" && styles.langChipOn]}
              onPress={() => setLocale("fr")}
            >
              <Text style={[styles.langChipText, locale === "fr" && styles.langChipTextOn]}>{t("lang_french")}</Text>
            </Pressable>
          </View>

          {paragraphs.map((paragraph, index) => (
            <Text key={index} style={[styles.body, index === 0 && styles.bodyLead]}>
              {paragraph}
            </Text>
          ))}
        </ScrollView>

        <Pressable style={styles.cta} onPress={onContinue} accessibilityRole="button">
          <Text style={styles.ctaText}>{t("welcome_cta")}</Text>
        </Pressable>
      </Animated.View>
    </View>
  );
}

const styles = StyleSheet.create({
  root: {
    flex: 1,
    backgroundColor: "#06101F",
  },
  glowTop: {
    position: "absolute",
    top: -80,
    left: -40,
    width: 260,
    height: 260,
    borderRadius: 130,
    backgroundColor: "rgba(45, 212, 191, 0.18)",
  },
  glowBottom: {
    position: "absolute",
    bottom: -60,
    right: -50,
    width: 220,
    height: 220,
    borderRadius: 110,
    backgroundColor: "rgba(56, 189, 248, 0.12)",
  },
  inner: { flex: 1, paddingHorizontal: 22 },
  scroll: { flex: 1 },
  scrollContent: { paddingTop: 28, paddingBottom: 20 },
  brand: {
    fontSize: 34,
    fontWeight: "900",
    color: "#FFFFFF",
    letterSpacing: -0.6,
  },
  tagline: {
    marginTop: 8,
    marginBottom: 18,
    fontSize: 15,
    color: CF.cyan,
    fontWeight: "700",
  },
  langRow: { flexDirection: "row", gap: 8, marginBottom: 20 },
  langChip: {
    paddingHorizontal: 14,
    paddingVertical: 8,
    borderRadius: 999,
    borderWidth: 1,
    borderColor: "rgba(255,255,255,0.22)",
    backgroundColor: "rgba(255,255,255,0.06)",
  },
  langChipOn: {
    borderColor: CF.cyan,
    backgroundColor: "rgba(45, 212, 191, 0.18)",
  },
  langChipText: { color: "#B8D4D0", fontWeight: "700", fontSize: 13 },
  langChipTextOn: { color: "#FFFFFF" },
  body: {
    fontSize: 15,
    lineHeight: 24,
    color: "#CBD5E1",
    marginBottom: 14,
  },
  bodyLead: { color: "#E2E8F0", fontWeight: "500" },
  cta: {
    marginBottom: 8,
    backgroundColor: CF.cyan,
    borderRadius: 14,
    paddingVertical: 16,
    alignItems: "center",
  },
  ctaText: { color: "#042F2E", fontWeight: "900", fontSize: 16 },
});
